import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useMemo } from 'react';

import type { User } from '@/utils/session/fetch-user';

import { queryKeys } from '@/hooks/api/query-keys';
import { DEFAULT_PROFILE_PICTURE_URL } from '@/utils/constants';
import { cx } from '@/utils/cx';
import { dayjs } from '@/utils/date';

import type { AvatarProps } from '../base/avatar/avatar';

import { AvatarAddButton } from '../base/avatar/base-components';
import { AvatarEditButton } from '../base/avatar/base-components/avatar-edit-button';

interface AvatarProfileCardProps extends AvatarProps {
  user?: User | null;
}

export const AvatarProfileCard = ({
  className,
  user,
  size = 'lg',
  ...props
}: AvatarProfileCardProps) => {
  const queryClient = useQueryClient();
  const router = useRouter();

  const greeting = useMemo(() => {
    if (!user?.timezone) return t`Hello`;

    const hour = dayjs().tz(user.timezone).hour();

    if (hour < 12) return t`Morning`;
    if (hour < 18) return t`Afternoon`;

    return t`Evening`;
  }, [user?.timezone]);

  if (!user) {
    return <AvatarProfileCardSkeleton className={className} />;
  }

  const handleUploadSuccess = () => {
    queryClient.removeQueries({
      queryKey: queryKeys.session.current(),
    });
    void router.invalidate();
  };

  return (
    <figure
      className={cx(
        'group flex min-w-0 flex-1 items-center',
        'gap-3',
        className,
      )}
    >
      {user.picture && user.picture !== DEFAULT_PROFILE_PICTURE_URL ? (
        <AvatarEditButton
          userId={user.id}
          src={user.picture}
          size={size}
          {...props}
          verified={user.isEmailVerified}
          onUploadSuccess={handleUploadSuccess}
        />
      ) : (
        <AvatarAddButton
          size="lg"
          userId={user.id}
          onUploadSuccess={handleUploadSuccess}
        />
      )}
      <figcaption className="min-w-0 flex-1">
        <p className={cx('text-primary', 'text-md font-semibold')}>
          <Trans>
            {greeting} {user.firstName},
          </Trans>
        </p>
        <p className={cx('truncate text-tertiary', 'text-sm')}>ID: {user.id}</p>
      </figcaption>
    </figure>
  );
};

export function AvatarProfileCardSkeleton({
  className,
}: {
  className?: string;
}) {
  return (
    <figure
      className={cx(
        'group flex min-w-0 flex-1 items-center',
        'gap-3',
        className,
      )}
    >
      {/* Avatar skeleton */}
      <div className="size-12 rounded-full bg-alpha-black-10" />
      <figcaption className="min-w-0 flex-1 space-y-2">
        {/* Greeting skeleton */}
        <div className="h-5 w-32 rounded bg-alpha-black-10" />
        {/* ID skeleton */}
        <div className="h-4 w-24 rounded bg-alpha-black-10" />
      </figcaption>
    </figure>
  );
}
